/**
 * Audio Processing Utilities for Tesla Compatibility
 *
 * Tesla Lock Sound Requirements (sourced from lib/tesla-facts.ts):
 * - Format: WAV (required — Tesla only plays .wav named LockChime.wav)
 * - Sample Rate: 44.1kHz (recommended best-practice, not a published Tesla spec)
 * - Bit Depth: 16-bit (recommended)
 * - Max Duration: 5 seconds
 * - Max File Size: under 1 MB (see TESLA_FACTS.audio.recommended.maxFileSize)
 */

import { TESLA_FACTS } from './tesla-facts'

export interface AudioMetadata {
  duration: number
  sampleRate: number
  numberOfChannels: number
  length: number
  bitDepth?: number
  /** Rate the uploaded file declares. `sampleRate` is the decoded rate, which follows the browser's audio context. */
  sourceSampleRate?: number
}

export interface TeslaCompatibilityReport {
  isCompatible: boolean
  issues: string[]
  warnings: string[]
  metadata: AudioMetadata | null
  fileSize?: number
}

export const TESLA_REQUIREMENTS = {
  MAX_DURATION: 5, // seconds
  MAX_FILE_SIZE: 1 * 1024 * 1024, // 1MB in bytes (Tesla official limit)
  SAMPLE_RATE: 44100, // 44.1kHz
  BIT_DEPTH: 16,
  TARGET_DB_MIN: 60,
  TARGET_DB_MAX: 85,
  CHANNELS: 2, // Stereo
  FILENAME: 'LockChime.wav', // Required filename for Tesla
} as const

/**
 * Validates if an audio file is Tesla compatible
 */
export async function validateTeslaCompatibility(
  file: File
): Promise<TeslaCompatibilityReport> {
  const issues: string[] = []
  const warnings: string[] = []

  // Check file size
  if (file.size > TESLA_REQUIREMENTS.MAX_FILE_SIZE) {
    issues.push(
      `File size (${(file.size / 1024 / 1024).toFixed(2)} MB) exceeds ${TESLA_FACTS.audio.recommended.maxFileSize}`
    )
  }

  // Check file format
  const extension = file.name.toLowerCase().split('.').pop()
  if (extension !== 'wav') {
    warnings.push('File is not WAV format - conversion required')
  }

  try {
    const audioBuffer = await decodeAudioFile(file)
    const metadata = getAudioMetadataFromBuffer(audioBuffer)

    // Check duration
    if (metadata.duration > TESLA_REQUIREMENTS.MAX_DURATION) {
      issues.push(
        `Duration (${metadata.duration.toFixed(2)}s) exceeds 5 second limit`
      )
    }

    // Check sample rate
    if (metadata.sampleRate !== TESLA_REQUIREMENTS.SAMPLE_RATE) {
      warnings.push(
        `Sample rate (${metadata.sampleRate}Hz) should be 44.1kHz - resampling required`
      )
    }

    // Check channels
    if (metadata.numberOfChannels !== TESLA_REQUIREMENTS.CHANNELS) {
      warnings.push(
        `Audio is ${metadata.numberOfChannels === 1 ? 'mono' : 'multi-channel'} - converting to stereo recommended`
      )
    }

    return {
      isCompatible: issues.length === 0,
      issues,
      warnings,
      metadata,
      fileSize: file.size,
    }
  } catch (error) {
    issues.push(`Failed to decode audio file: ${error instanceof Error ? error.message : 'Unknown error'}`)
    return {
      isCompatible: false,
      issues,
      warnings,
      metadata: null,
      fileSize: file.size,
    }
  }
}

/**
 * §19.3: a decode failure has two distinct causes that read very differently
 * to a user, and docs/ANTI_SLOP.md rule 5 forbids collapsing them into one
 * generic message. `decodeAudioFile` distinguishes them by sniffing the raw
 * bytes for a known container signature BEFORE handing off to
 * decodeAudioData: a header that doesn't match any known audio container is
 * "torn" (renamed, truncated, or never audio to begin with); a header that
 * does match but still fails to decode is "corrupt" (the container is
 * readable, the payload inside it is not).
 */
export class AudioHeaderError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AudioHeaderError'
  }
}

/**
 * Recognizes the magic bytes of every format `file-dropzone.tsx` accepts
 * (MP3, WAV, OGG, M4A/AAC, WebM). Not a full parser — just enough to tell
 * "this could plausibly be audio" from "this file's header is torn or was
 * never audio at all".
 */
function hasRecognizedAudioHeader(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false
  const ascii = (start: number, len: number) =>
    String.fromCharCode(...bytes.subarray(start, start + len))

  if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WAVE') return true // WAV
  if (ascii(0, 4) === 'OggS') return true // OGG
  if (ascii(4, 4) === 'ftyp') return true // M4A/AAC (MP4 container)
  if (ascii(0, 3) === 'ID3') return true // MP3 with an ID3 tag
  if (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) return true // MP3 frame sync, no tag
  if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) return true // WebM/EBML

  return false
}

/**
 * Decodes an audio file into an AudioBuffer
 */
export async function decodeAudioFile(file: File): Promise<AudioBuffer> {
  const arrayBuffer = await file.arrayBuffer()

  const headerBytes = new Uint8Array(arrayBuffer.slice(0, 16))
  if (!hasRecognizedAudioHeader(headerBytes)) {
    throw new AudioHeaderError(
      `${file.name}'s header does not match a known audio format. It may have been renamed from something else, or truncated before the header finished writing.`
    )
  }

  const audioContext = new AudioContext()

  try {
    // audio-egress-allow: decode helper for the trimmer, used after upload
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    return audioBuffer
  } finally {
    await audioContext.close()
  }
}

/**
 * Extracts metadata from an AudioBuffer
 */
export function getAudioMetadataFromBuffer(audioBuffer: AudioBuffer): AudioMetadata {
  return {
    duration: audioBuffer.duration,
    sampleRate: audioBuffer.sampleRate,
    numberOfChannels: audioBuffer.numberOfChannels,
    length: audioBuffer.length,
  }
}

/**
 * Gets metadata from a File
 */
async function getAudioMetadata(file: File): Promise<AudioMetadata> {
  const audioBuffer = await decodeAudioFile(file)
  return getAudioMetadataFromBuffer(audioBuffer)
}

/**
 * Normalizes audio volume using peak normalization
 * This finds the maximum peak and scales to reach the target level
 * @param audioBuffer - The audio to normalize
 * @param targetPeakDb - Target peak level in dBFS (must be <= 0; e.g. -1 = 1 dB headroom).
 *   Positive values would require gain > 1 when peak is already full-scale, causing clipping;
 *   they are rejected with an error so callers don't silently destroy audio.
 */
export function normalizeAudioVolume(
  audioBuffer: AudioBuffer,
  targetPeakDb: number = -1 // -1 dBFS below clipping by default
): AudioBuffer {
  if (targetPeakDb > 0) {
    throw new RangeError(
      `normalizeAudioVolume: targetPeakDb must be <= 0 dBFS (got ${targetPeakDb}). ` +
      'Positive values mean "louder than digital full-scale", which causes clipping.'
    )
  }

  // Find the maximum peak across all channels
  let maxPeak = 0
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel)
    for (let i = 0; i < channelData.length; i++) {
      const absValue = Math.abs(channelData[i])
      if (absValue > maxPeak) {
        maxPeak = absValue
      }
    }
  }

  // Avoid division by zero for silent audio
  if (maxPeak === 0) {
    return audioBuffer
  }

  // Calculate gain needed to reach target peak
  // targetPeakDb of -1 means we want peak at 10^(-1/20) ≈ 0.891
  const targetPeakLinear = Math.pow(10, targetPeakDb / 20)
  const gain = targetPeakLinear / maxPeak

  // Use new AudioBuffer() instead of OfflineAudioContext.createBuffer() — the
  // OfflineAudioContext instance is created only for rendering (resampleAudio),
  // not as a buffer factory for synchronous operations.
  const normalizedBuffer = new AudioBuffer({
    numberOfChannels: audioBuffer.numberOfChannels,
    length: audioBuffer.length,
    sampleRate: audioBuffer.sampleRate,
  })

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const inputData = audioBuffer.getChannelData(channel)
    const outputData = normalizedBuffer.getChannelData(channel)

    for (let i = 0; i < inputData.length; i++) {
      // Apply gain - this will never clip because we calculated from peak
      outputData[i] = inputData[i] * gain
    }
  }

  return normalizedBuffer
}

// ---------------------------------------------------------------------------
// Loudness engine (RMS-based)
// ---------------------------------------------------------------------------

/**
 * Loudness presets for Chime Studio.
 * Values are target RMS dBFS levels — NOT claimed to match any Tesla measurement.
 * Copy must say "typical lock-chime loudness" or "typical quiet/normal/loud level",
 * never "matched to Tesla's stock chime" or "−18 dBFS Tesla level" (unsourced).
 */
export const LOUDNESS_PRESETS = {
  quiet:  { label: 'Quiet',  targetRmsDb: -26, description: 'Subtle, for shorter or punchy sounds' },
  normal: { label: 'Normal', targetRmsDb: -20, description: 'Typical lock-chime loudness' },
  loud:   { label: 'Loud',   targetRmsDb: -14, description: 'Full-bodied, for brief clips' },
} as const

export type LoudnessPreset = keyof typeof LOUDNESS_PRESETS

/** Fine-tune gain range in dB (offset applied on top of the preset RMS level). */
export const FINE_TUNE_GAIN_RANGE = { min: -30, max: 6 } as const

/**
 * Measures the RMS loudness of an AudioBuffer in dBFS.
 * Returns -Infinity for a silent buffer (all zeros).
 * Uses the root-mean-square across all channels combined.
 */
export function measureLoudnessDb(buffer: AudioBuffer): number {
  let sumSquares = 0
  let sampleCount = 0

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel)
    for (let i = 0; i < data.length; i++) {
      sumSquares += data[i] * data[i]
      sampleCount++
    }
  }

  if (sampleCount === 0) return -Infinity
  const rms = Math.sqrt(sumSquares / sampleCount)
  if (rms === 0) return -Infinity
  return 20 * Math.log10(rms)
}

/**
 * Normalizes an AudioBuffer to a target RMS loudness level (in dBFS),
 * with a hard peak clamp so the output never exceeds -1 dBFS.
 *
 * @param buffer      - Source audio.
 * @param targetRmsDb - Desired RMS level in dBFS (e.g. -20 for normal loudness).
 * @returns A new AudioBuffer at the target loudness.
 */
export function normalizeToLoudness(
  buffer: AudioBuffer,
  targetRmsDb: number
): AudioBuffer {
  const currentRmsDb = measureLoudnessDb(buffer)
  if (!isFinite(currentRmsDb)) {
    // Silent buffer — nothing to normalize
    return buffer
  }

  // Gain needed to reach target RMS
  const gainDb = targetRmsDb - currentRmsDb
  let gainLinear = Math.pow(10, gainDb / 20)

  // Peak clamp: find the current peak and ensure applying gainLinear won't
  // push any sample above -1 dBFS (linear: 10^(-1/20) ≈ 0.891).
  const peakCeiling = Math.pow(10, -1 / 20)
  let maxPeak = 0
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel)
    for (let i = 0; i < data.length; i++) {
      const abs = Math.abs(data[i])
      if (abs > maxPeak) maxPeak = abs
    }
  }

  if (maxPeak > 0) {
    const maxAllowedGain = peakCeiling / maxPeak
    gainLinear = Math.min(gainLinear, maxAllowedGain)
  }

  // Build output buffer
  const output = new AudioBuffer({
    numberOfChannels: buffer.numberOfChannels,
    length: buffer.length,
    sampleRate: buffer.sampleRate,
  })

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const inData = buffer.getChannelData(channel)
    const outData = output.getChannelData(channel)
    for (let i = 0; i < inData.length; i++) {
      outData[i] = inData[i] * gainLinear
    }
  }

  return output
}

/**
 * Trims audio to a specific duration
 *
 * @throws {RangeError} if the resulting duration would be zero or negative
 *   (e.g. startTime >= endTime). Callers such as the Chime Studio "Export"
 *   button should disable the export action when trimDuration <= 0 rather
 *   than letting this throw at runtime.
 */
export function trimAudio(
  audioBuffer: AudioBuffer,
  startTime: number = 0,
  endTime?: number
): AudioBuffer {
  const sampleRate = audioBuffer.sampleRate
  const startSample = Math.floor(startTime * sampleRate)
  const endSample = endTime !== undefined
    ? Math.floor(endTime * sampleRate)
    : audioBuffer.length

  const trimmedLength = endSample - startSample

  if (trimmedLength <= 0) {
    throw new RangeError(
      `trimAudio: resulting duration is zero or negative ` +
      `(startTime=${startTime}, endTime=${endTime ?? 'end'}, ` +
      `startSample=${startSample}, endSample=${endSample}). ` +
      'Disable export when the trim selection has no duration.'
    )
  }

  const trimmedBuffer = new AudioBuffer({
    numberOfChannels: audioBuffer.numberOfChannels,
    length: trimmedLength,
    sampleRate,
  })

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const inputData = audioBuffer.getChannelData(channel)
    const outputData = trimmedBuffer.getChannelData(channel)

    for (let i = 0; i < trimmedLength; i++) {
      const sourceIndex = startSample + i
      if (sourceIndex < inputData.length) {
        outputData[i] = inputData[sourceIndex]
      }
    }
  }

  return trimmedBuffer
}

/**
 * Converts audio to mono or stereo
 */
export function convertChannels(
  audioBuffer: AudioBuffer,
  targetChannels: 1 | 2
): AudioBuffer {
  if (audioBuffer.numberOfChannels === targetChannels) {
    return audioBuffer
  }

  const convertedBuffer = new AudioBuffer({
    numberOfChannels: targetChannels,
    length: audioBuffer.length,
    sampleRate: audioBuffer.sampleRate,
  })

  if (targetChannels === 1) {
    // Convert to mono by averaging all channels
    const outputData = convertedBuffer.getChannelData(0)

    for (let i = 0; i < audioBuffer.length; i++) {
      let sum = 0
      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        sum += audioBuffer.getChannelData(channel)[i]
      }
      outputData[i] = sum / audioBuffer.numberOfChannels
    }
  } else {
    // Convert to stereo
    if (audioBuffer.numberOfChannels === 1) {
      // Duplicate mono to both channels
      const inputData = audioBuffer.getChannelData(0)
      convertedBuffer.getChannelData(0).set(inputData)
      convertedBuffer.getChannelData(1).set(inputData)
    } else {
      // Downmix multiple channels to stereo
      const leftData = convertedBuffer.getChannelData(0)
      const rightData = convertedBuffer.getChannelData(1)

      for (let i = 0; i < audioBuffer.length; i++) {
        leftData[i] = audioBuffer.getChannelData(0)[i]
        rightData[i] = audioBuffer.getChannelData(
          Math.min(1, audioBuffer.numberOfChannels - 1)
        )[i]
      }
    }
  }

  return convertedBuffer
}

/**
 * Resamples audio to target sample rate
 */
export async function resampleAudio(
  audioBuffer: AudioBuffer,
  targetSampleRate: number
): Promise<AudioBuffer> {
  if (audioBuffer.sampleRate === targetSampleRate) {
    return audioBuffer
  }

  const ratio = targetSampleRate / audioBuffer.sampleRate
  const newLength = Math.floor(audioBuffer.length * ratio)

  const offlineContext = new OfflineAudioContext(
    audioBuffer.numberOfChannels,
    newLength,
    targetSampleRate
  )

  const source = offlineContext.createBufferSource()
  source.buffer = audioBuffer
  source.connect(offlineContext.destination)
  source.start(0)

  return await offlineContext.startRendering()
}

/**
 * Converts AudioBuffer to WAV format with 16-bit depth
 */
export function convertToWav(audioBuffer: AudioBuffer): Blob {
  const numberOfChannels = audioBuffer.numberOfChannels
  const sampleRate = audioBuffer.sampleRate
  const format = 1 // PCM
  const bitDepth = 16

  const bytesPerSample = bitDepth / 8
  const blockAlign = numberOfChannels * bytesPerSample

  const samples = audioBuffer.length
  const dataSize = samples * blockAlign
  const bufferSize = 44 + dataSize

  const buffer = new ArrayBuffer(bufferSize)
  const view = new DataView(buffer)

  // Write WAV header
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, format, true)
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true) // byte rate
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)
  writeString(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  // Write audio data (interleaved)
  let offset = 44
  const channels: Float32Array[] = []

  for (let i = 0; i < numberOfChannels; i++) {
    channels.push(audioBuffer.getChannelData(i))
  }

  for (let i = 0; i < samples; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, channels[channel][i]))
      const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7fff
      view.setInt16(offset, int16, true)
      offset += 2
    }
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * Helper function to write strings to DataView
 */
function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i))
  }
}

/**
 * All-in-one Tesla optimization function
 * Applies all necessary transformations to make audio Tesla-compatible
 *
 * @param options.targetDb - Target peak level in dBFS (must be <= 0). Defaults to -1
 *   (1 dB headroom below digital full-scale). The TESLA_REQUIREMENTS.TARGET_DB_MIN/MAX
 *   constants (60–85) describe perceived SPL targets, not dBFS values; do not pass
 *   those values here directly.
 */
export async function optimizeForTesla(
  file: File,
  options: {
    maxDuration?: number
    /** Target peak in dBFS (must be <= 0). Default: -1. */
    targetDb?: number
    startTime?: number
  } = {}
): Promise<Blob> {
  const {
    maxDuration = TESLA_REQUIREMENTS.MAX_DURATION,
    targetDb = -1,
    startTime = 0,
  } = options

  // Decode audio
  let audioBuffer = await decodeAudioFile(file)

  // Trim to max duration if needed
  if (audioBuffer.duration > maxDuration) {
    audioBuffer = trimAudio(audioBuffer, startTime, startTime + maxDuration)
  } else if (startTime > 0) {
    audioBuffer = trimAudio(audioBuffer, startTime)
  }

  // Resample to 44.1kHz if needed
  if (audioBuffer.sampleRate !== TESLA_REQUIREMENTS.SAMPLE_RATE) {
    audioBuffer = await resampleAudio(audioBuffer, TESLA_REQUIREMENTS.SAMPLE_RATE)
  }

  // Convert to stereo if needed
  if (audioBuffer.numberOfChannels !== TESLA_REQUIREMENTS.CHANNELS) {
    audioBuffer = convertChannels(audioBuffer, TESLA_REQUIREMENTS.CHANNELS)
  }

  // Normalize volume — normalizeAudioVolume guards targetDb > 0 internally.
  audioBuffer = normalizeAudioVolume(audioBuffer, targetDb)

  // Convert to WAV
  return convertToWav(audioBuffer)
}

/**
 * Calculate estimated file size after optimization
 */
export function estimateOptimizedSize(
  metadata: AudioMetadata,
  maxDuration: number = TESLA_REQUIREMENTS.MAX_DURATION
): number {
  const duration = Math.min(metadata.duration, maxDuration)
  const sampleRate = TESLA_REQUIREMENTS.SAMPLE_RATE
  const channels = TESLA_REQUIREMENTS.CHANNELS
  const bitDepth = TESLA_REQUIREMENTS.BIT_DEPTH

  const samples = duration * sampleRate
  const dataSize = samples * channels * (bitDepth / 8)
  const totalSize = 44 + dataSize // 44 bytes for WAV header

  return totalSize
}

/**
 * Apply fade in effect to audio
 * Uses an equal-power curve for smooth transitions
 */
export function applyFadeIn(
  audioBuffer: AudioBuffer,
  duration: number = 0.1 // seconds
): AudioBuffer {
  if (duration <= 0) return audioBuffer

  const sampleRate = audioBuffer.sampleRate
  const fadeSamples = Math.min(
    Math.floor(duration * sampleRate),
    audioBuffer.length
  )

  // Create a new buffer to avoid modifying the original
  const fadedBuffer = new AudioBuffer({
    numberOfChannels: audioBuffer.numberOfChannels,
    length: audioBuffer.length,
    sampleRate,
  })

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const inputData = audioBuffer.getChannelData(channel)
    const outputData = fadedBuffer.getChannelData(channel)

    for (let i = 0; i < audioBuffer.length; i++) {
      if (i < fadeSamples) {
        // Equal-power fade curve: sin(x * PI/2) where x goes from 0 to 1
        const fadePosition = i / fadeSamples
        const fadeGain = Math.sin(fadePosition * Math.PI / 2)
        outputData[i] = inputData[i] * fadeGain
      } else {
        outputData[i] = inputData[i]
      }
    }
  }

  return fadedBuffer
}

/**
 * Apply fade out effect to audio
 * Uses an equal-power curve for smooth transitions
 */
export function applyFadeOut(
  audioBuffer: AudioBuffer,
  duration: number = 0.1 // seconds
): AudioBuffer {
  if (duration <= 0) return audioBuffer

  const sampleRate = audioBuffer.sampleRate
  const fadeSamples = Math.min(
    Math.floor(duration * sampleRate),
    audioBuffer.length
  )

  const fadeStartSample = audioBuffer.length - fadeSamples

  // Create a new buffer to avoid modifying the original
  const fadedBuffer = new AudioBuffer({
    numberOfChannels: audioBuffer.numberOfChannels,
    length: audioBuffer.length,
    sampleRate,
  })

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const inputData = audioBuffer.getChannelData(channel)
    const outputData = fadedBuffer.getChannelData(channel)

    for (let i = 0; i < audioBuffer.length; i++) {
      if (i >= fadeStartSample) {
        // Equal-power fade curve: cos(x * PI/2) where x goes from 0 to 1
        const fadePosition = (i - fadeStartSample) / fadeSamples
        const fadeGain = Math.cos(fadePosition * Math.PI / 2)
        outputData[i] = inputData[i] * fadeGain
      } else {
        outputData[i] = inputData[i]
      }
    }
  }

  return fadedBuffer
}

/**
 * Enhanced Tesla optimization with fade in/out and loudness normalization.
 * Applies all necessary transformations to make audio Tesla-compatible.
 *
 * Loudness path (preferred): pass `targetRmsDb` (from a LOUDNESS_PRESETS entry)
 * and optionally `fineTuneGainDb` (−30…+6 dB offset). This uses RMS-based
 * normalizeToLoudness() with a −1 dBFS peak clamp.
 *
 * Legacy path (backward-compat): pass `volumePercent` (0–100); maps to a peak
 * dB target the same way the original implementation did.
 *
 * If both are provided, the loudness path takes precedence.
 */
export async function optimizeForTeslaWithFades(
  audioBuffer: AudioBuffer,
  options: {
    startTime?: number
    endTime?: number
    /** Legacy: 0-100, where 100 = full volume (-1 dB peak). Prefer targetRmsDb. */
    volumePercent?: number
    /** Loudness target in dBFS RMS (from LOUDNESS_PRESETS[preset].targetRmsDb). */
    targetRmsDb?: number
    /** Fine-tune offset in dB applied after RMS normalization (−30…+6). */
    fineTuneGainDb?: number
    fadeIn?: number  // seconds (0-0.5)
    fadeOut?: number // seconds (0-0.5)
  } = {}
): Promise<Blob> {
  const {
    startTime = 0,
    endTime,
    volumePercent = 100,
    targetRmsDb,
    fineTuneGainDb = 0,
    fadeIn = 0,
    fadeOut = 0,
  } = options

  let buffer = audioBuffer

  // Trim to selection
  const effectiveEnd = endTime ?? Math.min(startTime + TESLA_REQUIREMENTS.MAX_DURATION, buffer.duration)
  if (startTime > 0 || effectiveEnd < buffer.duration) {
    buffer = trimAudio(buffer, startTime, effectiveEnd)
  }

  // Apply fade in
  if (fadeIn > 0) {
    buffer = applyFadeIn(buffer, fadeIn)
  }

  // Apply fade out
  if (fadeOut > 0) {
    buffer = applyFadeOut(buffer, fadeOut)
  }

  // Resample to 44.1kHz if needed
  if (buffer.sampleRate !== TESLA_REQUIREMENTS.SAMPLE_RATE) {
    buffer = await resampleAudio(buffer, TESLA_REQUIREMENTS.SAMPLE_RATE)
  }

  // Convert to stereo if needed
  if (buffer.numberOfChannels !== TESLA_REQUIREMENTS.CHANNELS) {
    buffer = convertChannels(buffer, TESLA_REQUIREMENTS.CHANNELS)
  }

  if (targetRmsDb !== undefined) {
    // Loudness path: RMS normalization + fine-tune offset, with peak clamp built-in.
    const effectiveRmsDb = targetRmsDb + fineTuneGainDb
    buffer = normalizeToLoudness(buffer, effectiveRmsDb)
  } else {
    // Legacy path: convert volume percent to peak dB.
    // 100% = -1 dB (full volume), 50% = -10 dB, 0% = -20 dB (very quiet).
    const targetPeakDb = -1 - ((100 - volumePercent) * 0.19)
    buffer = normalizeAudioVolume(buffer, targetPeakDb)
  }

  // Convert to WAV
  return convertToWav(buffer)
}
